<script>
    import { page } from "$app/stores";
    import SidebarGroup from "/components/SidebarGroup.svelte";
    import SidebarMenuItem from "/components/SidebarMenuItem.svelte";
    import SidebarToggle from "/components/SidebarToggle.svelte";
    import { onMount } from "svelte";

    import { logout } from "/util";
    import fetch from "/util";
    import { hiddenMenus } from "/stores";
    import { isMenuVisible } from "/common/permissions.js";
    import { REPORT_MENUS } from "/common/reportMenus.js";
    import { menuFavorites, loadMenuFavorites, setMenuFavorite } from "/stores/menuFavorites.js";
    import { showAlert } from "/stores/alert.js";
    import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from "lucide-svelte";

    let user = JSON.parse(localStorage.getItem("user"));
    let workspaceStatus = "active"; // default to active
    let profileOpen = false;
    let menuCommand = null;
    // 화면에 렌더된 그룹 id → 열림 여부. 그룹을 하나씩 열고 닫아도 '모두 열기/닫기' 라벨이 실제 상태를 따른다.
    let groupOpen = {};

    // $hiddenMenus에 의존하는 반응형 선언으로 둬야 config가 늦게 로드돼도(F5 등)
    // {#if showMenu(...)} 블록이 재평가되어 숨김이 반영된다.
    $: showMenu = (path) => isMenuVisible(path, $hiddenMenus, user.Type);
    // 보고서 진입점은 노출 가능한 첫 보고서로 보낸다. 특정 보고서 하나가 숨겨져도 나머지가 도달 불가가 되지 않도록 한다.
    $: visibleReports = REPORT_MENUS.filter((m) => showMenu(m.path));
    $: allMenusOpen = Object.keys(groupOpen).length > 0 && Object.values(groupOpen).every(Boolean);

    function toggleProfile(e) {
        e.stopPropagation();
        profileOpen = !profileOpen;
    }

    function closeMenus() {
        profileOpen = false;
    }

    function toggleAllMenus() {
        menuCommand = { open: !allMenusOpen };
    }

    function setGroupOpen(id, open) {
        if (groupOpen[id] === open) return;
        if (open === null) delete groupOpen[id];
        else groupOpen[id] = open;
        groupOpen = groupOpen;
    }

    async function toggleFavorite(path, selected) {
        try {
            await setMenuFavorite(path, selected);
        } catch (error) {
            await showAlert(error.message || "즐겨찾기를 저장할 수 없습니다.");
        }
    }

    onMount(async () => {
        try {
            await loadMenuFavorites(user.ID);
        } catch (error) {
            console.error("즐겨찾기 불러오기 실패:", error);
        }
        try {
            const response = await fetch(`/workspaces/${user.WorkspaceID}`);
            if (response.ok) {
                const workspace = await response.json();
                workspaceStatus = workspace.Status;
            }
        } catch (error) {
            console.error("Error loading workspace:", error);
        }
    });
</script>

<svelte:window on:click={closeMenus} on:keydown={(e) => { if (e.key === "Escape") closeMenus(); }} />

<nav id="desktop-sidebar" class="desktop-sidebar" aria-label="파트너 메뉴">
    <div class="sidebar-brand">
    <img id="gnb-logo" alt="" class="nav-logo" src="/assets/images/gnb_logo.png" />
    <SidebarToggle />
    </div>
    <div id="sidebar-navigation" class="sidebar-scroll">
    {#if workspaceStatus === "active"}
    <button id="btn-toggle-all-menus" class="sidebar-menu-toggle-all textButton" type="button" aria-expanded={allMenusOpen} on:click={toggleAllMenus}>
        {#if allMenusOpen}<ChevronsDownUp size={15} />{:else}<ChevronsUpDown size={15} />{/if}
        <span>{allMenusOpen ? "모두 닫기" : "모두 열기"}</span>
    </button>
    {/if}
    {#if !$hiddenMenus.has('dashboard')}
    <a id="gnb-dashboard" href="/partner/dashboard" aria-current={$page.url.pathname === "/partner/dashboard" ? 'page' : undefined} class="sidebar-home-link">대시보드</a>
    {/if}
    {#if workspaceStatus === "active"}
    {#if !$hiddenMenus.has('basic-info')}
    <SidebarGroup id="gnb-basic-info" label="기초 정보" paths={["/partner/operation/company","/partner/members","/partner/operation/warehouse","/partner/operation/custom-equipment-model","/partner/operation/consumables","/partner/customer"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                {#if showMenu('/partner/operation/company')}
                <SidebarMenuItem path="/partner/operation/company" label="관리 사업자" current={$page.url.pathname === "/partner/operation/company"} favorite={$menuFavorites.includes('/partner/operation/company')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/members')}
                <SidebarMenuItem path="/partner/members" label="구성원" current={$page.url.pathname === "/partner/members"} favorite={$menuFavorites.includes('/partner/members')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/operation/warehouse')}
                <SidebarMenuItem path="/partner/operation/warehouse" label="창고" current={$page.url.pathname === "/partner/operation/warehouse"} favorite={$menuFavorites.includes('/partner/operation/warehouse')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/operation/custom-equipment-model')}
                <SidebarMenuItem path="/partner/operation/custom-equipment-model" label="사용자 정의 모델" current={$page.url.pathname === "/partner/operation/custom-equipment-model"} favorite={$menuFavorites.includes('/partner/operation/custom-equipment-model')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/operation/consumables')}
                <SidebarMenuItem path="/partner/operation/consumables" label="부소모품 코드" current={$page.url.pathname === "/partner/operation/consumables"} favorite={$menuFavorites.includes('/partner/operation/consumables')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/customer')}
                <SidebarMenuItem path="/partner/customer" label="고객" current={$page.url.pathname === "/partner/customer"} favorite={$menuFavorites.includes('/partner/customer')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('assets')}
    <SidebarGroup id="gnb-assets" label="자산" paths={["/partner/resource/equipment","/partner/resource/consumables","/partner/resource/consumable-inventory"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                {#if showMenu('/partner/resource/equipment')}
                <SidebarMenuItem path="/partner/resource/equipment" label="복합기·프린터 현황" current={$page.url.pathname === "/partner/resource/equipment"} favorite={$menuFavorites.includes('/partner/resource/equipment')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/resource/consumables')}
                <SidebarMenuItem path="/partner/resource/consumables" label="부소모품 입출고" current={$page.url.pathname === "/partner/resource/consumables"} favorite={$menuFavorites.includes('/partner/resource/consumables')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/resource/consumable-inventory')}
                <SidebarMenuItem path="/partner/resource/consumable-inventory" label="부소모품 재고" current={$page.url.pathname === "/partner/resource/consumable-inventory"} favorite={$menuFavorites.includes('/partner/resource/consumable-inventory')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('collected-equipment')}
    <SidebarGroup id="gnb-collected-equipment" label="장비 관리" paths={["/partner/printer","/partner/printer/unregistered","/partner/printer/agents"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                {#if showMenu('/partner/printer')}
                <SidebarMenuItem path="/partner/printer" label="복합기·프린터 관리" current={$page.url.pathname === "/partner/printer"} favorite={$menuFavorites.includes('/partner/printer')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/printer/unregistered')}
                <SidebarMenuItem path="/partner/printer/unregistered" label="메일수집장비 등록" current={$page.url.pathname === "/partner/printer/unregistered"} favorite={$menuFavorites.includes('/partner/printer/unregistered')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/printer/agents')}
                <SidebarMenuItem path="/partner/printer/agents" label="고객 미할당 에이전트" current={$page.url.pathname === "/partner/printer/agents"} favorite={$menuFavorites.includes('/partner/printer/agents')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('monitoring')}
    <SidebarGroup id="gnb-monitoring" label="모니터링" paths={["/partner/monitoring/data/usage","/partner/monitoring/data/supplies","/partner/monitoring/customer"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                {#if showMenu('/partner/monitoring/data/usage')}
                <SidebarMenuItem path="/partner/monitoring/data/usage" label="사용량" current={$page.url.pathname === "/partner/monitoring/data/usage"} favorite={$menuFavorites.includes('/partner/monitoring/data/usage')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/monitoring/data/supplies')}
                <SidebarMenuItem path="/partner/monitoring/data/supplies" label="소모품" current={$page.url.pathname === "/partner/monitoring/data/supplies"} favorite={$menuFavorites.includes('/partner/monitoring/data/supplies')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/monitoring/customer')}
                <SidebarMenuItem path="/partner/monitoring/customer" label="고객현황" current={$page.url.pathname === "/partner/monitoring/customer"} favorite={$menuFavorites.includes('/partner/monitoring/customer')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('as')}
    <SidebarGroup id="gnb-as" label="A/S" paths={["/partner/as/register","/partner/as/progress","/partner/as/completed","/partner/as/consumable-usages"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                {#if showMenu('/partner/as/register')}
                <SidebarMenuItem path="/partner/as/register" label="접수" current={$page.url.pathname === "/partner/as/register"} favorite={$menuFavorites.includes('/partner/as/register')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/as/progress')}
                <SidebarMenuItem path="/partner/as/progress" label="진행" current={$page.url.pathname === "/partner/as/progress"} favorite={$menuFavorites.includes('/partner/as/progress')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/as/completed')}
                <SidebarMenuItem path="/partner/as/completed" label="완료" current={$page.url.pathname === "/partner/as/completed"} favorite={$menuFavorites.includes('/partner/as/completed')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/as/consumable-usages')}
                <SidebarMenuItem path="/partner/as/consumable-usages" label="부소모품 사용 현황" current={$page.url.pathname === "/partner/as/consumable-usages"} favorite={$menuFavorites.includes('/partner/as/consumable-usages')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('contract') && showMenu('/partner/contract/list')}
    <SidebarGroup id="gnb-contract" label="계약" paths={["/partner/contract/uncontracted-equipment","/partner/contract/list","/partner/contract/invoice","/partner/contract/invoice/issued","/partner/contract/sales","/partner/contract/sales/issued"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                {#if showMenu('/partner/contract/uncontracted-equipment')}
                <SidebarMenuItem path="/partner/contract/uncontracted-equipment" label="미계약 장비" current={$page.url.pathname === "/partner/contract/uncontracted-equipment"} favorite={$menuFavorites.includes('/partner/contract/uncontracted-equipment')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/contract/list')}
                <SidebarMenuItem path="/partner/contract/list" label="계약 목록" current={$page.url.pathname === "/partner/contract/list"} favorite={$menuFavorites.includes('/partner/contract/list')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/contract/invoice')}
                <SidebarMenuItem path="/partner/contract/invoice" label="계약 명세서 발행" current={$page.url.pathname === "/partner/contract/invoice"} favorite={$menuFavorites.includes('/partner/contract/invoice')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/contract/invoice/issued')}
                <SidebarMenuItem path="/partner/contract/invoice/issued" label="계약 명세서 완료" current={$page.url.pathname === "/partner/contract/invoice/issued"} favorite={$menuFavorites.includes('/partner/contract/invoice/issued')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/contract/sales')}
                <SidebarMenuItem path="/partner/contract/sales" label="판매/용역서비스 등록" current={$page.url.pathname === "/partner/contract/sales"} favorite={$menuFavorites.includes('/partner/contract/sales')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/contract/sales/issued')}
                <SidebarMenuItem path="/partner/contract/sales/issued" label="판매/용역서비스 완료" current={$page.url.pathname === "/partner/contract/sales/issued"} favorite={$menuFavorites.includes('/partner/contract/sales/issued')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('sales-evidence') && (showMenu('/partner/sales-evidence/tax-invoice') || showMenu('/partner/payment/cash-receipt') || showMenu('/partner/sales-evidence/completed'))}
    <SidebarGroup id="gnb-sales-evidence" label="매출증빙" paths={["/partner/sales-evidence/tax-invoice","/partner/payment/cash-receipt","/partner/sales-evidence/completed"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                {#if showMenu('/partner/sales-evidence/tax-invoice')}
                <SidebarMenuItem path="/partner/sales-evidence/tax-invoice" label="세금계산서" current={$page.url.pathname === "/partner/sales-evidence/tax-invoice"} favorite={$menuFavorites.includes('/partner/sales-evidence/tax-invoice')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/payment/cash-receipt')}
                <SidebarMenuItem path="/partner/payment/cash-receipt" label="현금영수증" current={$page.url.pathname === "/partner/payment/cash-receipt"} favorite={$menuFavorites.includes('/partner/payment/cash-receipt')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/sales-evidence/completed')}
                <SidebarMenuItem path="/partner/sales-evidence/completed" label="증빙 완료" current={$page.url.pathname === "/partner/sales-evidence/completed"} favorite={$menuFavorites.includes('/partner/sales-evidence/completed')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('payment') && showMenu('/partner/payment/account-lookup')}
    <SidebarGroup id="gnb-payment" label="계좌/CMS" paths={["/partner/payment/account-lookup","/partner/payment/cms-member","/partner/payment/cms-withdrawal"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                <SidebarMenuItem path="/partner/payment/account-lookup" label="계좌 입출금" current={$page.url.pathname === "/partner/payment/account-lookup"} favorite={$menuFavorites.includes('/partner/payment/account-lookup')} onToggleFavorite={toggleFavorite} />
                {#if showMenu('/partner/payment/cms-member')}
                <SidebarMenuItem path="/partner/payment/cms-member" label="CMS 회원 관리" current={$page.url.pathname === "/partner/payment/cms-member"} favorite={$menuFavorites.includes('/partner/payment/cms-member')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if showMenu('/partner/payment/cms-withdrawal')}
                <SidebarMenuItem path="/partner/payment/cms-withdrawal" label="CMS 출금" current={$page.url.pathname === "/partner/payment/cms-withdrawal"} favorite={$menuFavorites.includes('/partner/payment/cms-withdrawal')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('ledger') && showMenu('/partner/ledger/transaction')}
    <SidebarGroup id="gnb-ledger" label="원장" paths={["/partner/ledger/transaction","/partner/ledger/advance-payment"]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                <SidebarMenuItem path="/partner/ledger/transaction" label="거래 원장" current={$page.url.pathname === "/partner/ledger/transaction"} favorite={$menuFavorites.includes('/partner/ledger/transaction')} onToggleFavorite={toggleFavorite} />
                {#if showMenu('/partner/ledger/advance-payment')}
                <SidebarMenuItem path="/partner/ledger/advance-payment" label="선수금 원장" current={$page.url.pathname === "/partner/ledger/advance-payment"} favorite={$menuFavorites.includes('/partner/ledger/advance-payment')} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {#if !$hiddenMenus.has('task-mgmt') && (showMenu('/partner/penalty/billing') || visibleReports.length > 0)}
    <SidebarGroup id="gnb-task-mgmt" label="업무관리" paths={["/partner/penalty/billing", ...visibleReports.map(m => m.path)]} {menuCommand} onOpenChange={setGroupOpen}>
            <ul>
                {#if showMenu('/partner/penalty/billing')}
                <SidebarMenuItem path="/partner/penalty/billing" label="위약금 청구" current={$page.url.pathname === "/partner/penalty/billing"} favorite={$menuFavorites.includes('/partner/penalty/billing')} onToggleFavorite={toggleFavorite} />
                {/if}
                {#if visibleReports.length > 0}
                <SidebarMenuItem path={visibleReports[0].path} label="보고서" current={$page.url.pathname.startsWith('/partner/report/')} favorite={$menuFavorites.includes(visibleReports[0].path)} onToggleFavorite={toggleFavorite} />
                {/if}
            </ul>
    </SidebarGroup>
    {/if}
    {/if}

    {#if !$hiddenMenus.has('help')}
    <SidebarGroup id="gnb-help" label="고객지원" {menuCommand} onOpenChange={setGroupOpen}>
        <ul>
            <li><a href="https://cmyk.oqupie.com/portal/3342" target="_blank" rel="noopener noreferrer">도움말</a></li>
            <li><a href="https://cmyk.oqupie.com/portal/3342/request" target="_blank" rel="noopener noreferrer">1:1 문의</a></li>
            <!-- 설치파일은 저장소가 아니라 운영 서버 web/downloads/에 둔다. 새 버전은 같은 이름으로 파일만 교체한다. -->
            <li><a id="gnb-agent-installer" href="/downloads/CMYK_Setup.exe" download="CMYK_Setup.exe">에이전트 설치파일</a></li>
        </ul>
    </SidebarGroup>
    {/if}
    </div>

    <div id="sidebar-account" class="sidebar-profile">
        <div class="profile-menu">
            <button id="gnb-profile-btn" class="profile-btn" aria-expanded={profileOpen} aria-controls="gnb-profile-menu" on:click={toggleProfile}>
                <span class="profile-avatar">{(user.Email?.[0] ?? '?').toUpperCase()}</span>
                <span class="profile-identity"><span class="profile-name">{user.Name || user.Email}</span><span class="profile-caption">내 계정</span></span>
                <span class="profile-chevron" class:rotated={profileOpen}><ChevronDown size={14} /></span>
            </button>
            {#if profileOpen}
            <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
            <div id="gnb-profile-menu" class="profile-dropdown" role="presentation" on:click|stopPropagation>
                <div class="profile-dropdown-email">{user.Email}</div>
                <ul>
                    <li><a id="gnb-myinfo" href="/partner/myinfo" aria-current={$page.url.pathname === "/partner/myinfo" ? 'page' : undefined}  on:click={closeMenus}>내 정보</a></li>
                    <li><button id="gnb-logout" class="textButton profile-logout-btn" on:click={logout}>로그아웃</button></li>
                </ul>
            </div>
            {/if}
        </div>
    </div>
    </nav>
